/**
 * Wallet Service — credit balance, top-ups and credit payments.
 *
 * Credits are pegged 1:1 to ZAR (1 credit = R1.00). Users load credits via the
 * same Stitch redirect flow used for direct payments, then spend them on
 * bookings without leaving the app.
 */

import apiService from './api';

/**
 * Get the current user's wallet (balance + recent transactions)
 * @returns {Promise<Object>} - { id, balance, recent_transactions, ... }
 */
export async function getWallet() {
  try {
    return await apiService.request('/billing/wallet/');
  } catch (error) {
    console.error('Get wallet error:', error);
    throw error;
  }
}

/**
 * Get the full credit ledger, paginated
 * @param {Object} filters - { page, transaction_type }
 * @returns {Promise<Object>} - DRF paginated response
 */
export async function getWalletTransactions(filters = {}) {
  try {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    const endpoint = params
      ? `/billing/wallet/transactions/?${params}`
      : '/billing/wallet/transactions/';
    return await apiService.request(endpoint);
  } catch (error) {
    console.error('Get wallet transactions error:', error);
    throw error;
  }
}

/**
 * Start a wallet top-up. Returns a Stitch payment URL to redirect the user to.
 * Credits are only added once Stitch confirms the payment.
 *
 * Pass `settleChargeId` to have the credits immediately pay that charge once
 * they land. The link is stored on the top-up server-side, so the charge is
 * still settled if the user closes the tab on the Stitch page.
 *
 * @param {number|string} amount - Credits to load (1 credit = R1.00)
 * @param {Object} [options] - { settleChargeId, returnUrl, cancelUrl }
 * @returns {Promise<Object>} - { charge_id, payment_url, credits, ... }
 */
export async function createTopUp(amount, options = {}) {
  try {
    const origin = window.location.origin;
    return await apiService.request('/billing/wallet/topup/', {
      method: 'POST',
      body: JSON.stringify({
        amount: Number(amount).toFixed(2),
        settle_charge_id: options.settleChargeId || null,
        return_url: options.returnUrl || `${origin}/payment/return`,
        cancel_url: options.cancelUrl || `${origin}/wallet`
      })
    });
  } catch (error) {
    console.error('Create top-up error:', error);
    throw error;
  }
}

/**
 * Settle a pending charge from the credit balance.
 * Throws with `insufficientCredits` set when the balance doesn't cover it.
 *
 * @param {number} chargeId - Charge to pay
 * @returns {Promise<Object>} - { status, charge_id, balance, transaction }
 */
export async function payWithCredits(chargeId) {
  try {
    return await apiService.request('/billing/wallet/pay/', {
      method: 'POST',
      body: JSON.stringify({ charge_id: chargeId })
    });
  } catch (error) {
    if (error.status === 402) {
      error.insufficientCredits = true;
    }
    console.error('Pay with credits error:', error);
    throw error;
  }
}

/**
 * Format a credit amount for display, e.g. 250 -> "250.00 credits"
 * @param {number|string} credits
 * @returns {string}
 */
export function formatCredits(credits) {
  const value = Number(credits || 0);
  return `${value.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} credits`;
}

/**
 * Format credits as their rand equivalent (they're 1:1)
 * @param {number|string} credits
 * @returns {string}
 */
export function formatCreditsAsRand(credits) {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR'
  }).format(Number(credits || 0));
}
